// US-28: per product, whether each time-conditioned obligation (a rule with a severity schedule in the active config
// pack, such as FPR digital labelling) applies on its route, and from when it blocks. Read from the stored verdict and
// the config pack; no rule id or date is written here.
import { useTranslation } from 'react-i18next';
import type { ScheduledRuleDef } from '../lib/data.ts';
import type { Verdict } from '../lib/types.ts';
import { Msg, ResultTag } from './ui.tsx';

export function Obligations({ verdict, rules }: { verdict: Verdict; rules: ScheduledRuleDef[] }) {
  const { t } = useTranslation();
  const scheduled = rules.filter((r) => r.severity_schedule && r.severity_schedule.length > 0);
  if (scheduled.length === 0) return null;
  return (
    <section className="labelling">
      <h3>{t('obligations.title')}</h3>
      {scheduled.map((def) => {
        const rr = verdict.rule_results.find((r) => r.rule_id === def.rule_id);
        if (!rr) return null;
        const head = (
          <>
            <strong>{def.criterion}</strong> <code>{def.rule_id}</code>:{' '}
          </>
        );
        if (rr.result === 'not_applicable')
          return (
            <p key={def.rule_id}>
              {head}
              {t('obligations.not_applicable', { route: verdict.reg_route })}
              {rr.not_applicable_reason && (
                <span className="muted">
                  {' '}
                  (<Msg k={rr.not_applicable_reason} />)
                </span>
              )}
            </p>
          );
        return (
          <p key={def.rule_id}>
            {head}
            {t('obligations.applies', { route: verdict.reg_route })}{' '}
            {(def.severity_schedule ?? [])
              .map((s) =>
                s.from
                  ? t('obligations.from', { severity: t(`severity.${s.severity}`, s.severity), date: s.from })
                  : t('obligations.until', {
                      severity: t(`severity.${s.severity}`, s.severity),
                      date: s.until ?? '',
                    }),
              )
              .join('; ')}
            .{' '}
            {t('obligations.today', {
              date: verdict.evaluation_date,
              severity: t(`severity.${rr.severity_effective}`, rr.severity_effective),
            })}{' '}
            <ResultTag result={rr.result} />
          </p>
        );
      })}
    </section>
  );
}
